'use client';

import React, { useRef, useState, useMemo, useCallback, useLayoutEffect, useEffect } from 'react';
import styled from 'styled-components';
import { i18n } from '@/config/i18n';
import { theme } from '@/config/theme';
import type { ChartData, ChartLayout, ChartConfig } from '@/types/chart';
import {
  exitDocumentFullscreen,
  getDocumentFullscreenElement,
  requestElementFullscreen,
  type FullscreenDocumentLike,
  type FullscreenHTMLElement,
} from '@/utils/fullscreenDom';
import { isPlotlyAxisEmpty } from '@/utils/plotlyChartUtils';
import { Chart } from './Chart';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { ExpandIcon } from '../icons/ExpandIcon';
import { ChartSkeleton } from './ChartSkeleton';
import { CustomLegend } from './combined/CustomLegend';
import { Breadcrumbs } from '../ui-components';
import type { BarChartProps } from '@/types/barChart';

export type { BarChartData, BarChartProps } from '@/types/barChart';

const BarChartContainer = styled.div<{ $isFullscreen: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;

  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${theme.colors.white};
  `}
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChartDescription = styled.p`
  display: block;
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: ${theme.colors.gray[500]};
`;

const CompactTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`;

const CompactTitleText = styled.span`
  font-size: 14px;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.gray[900]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
`;

const ChartContent = styled.div<{ $minHeight?: number }>`
  padding: 8px 16px 0px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: ${({ $minHeight }) => ($minHeight ? `${$minHeight}px` : '250px')};
  max-height: 100%;
  overflow: hidden;

  & > div {
    flex: 1;
    height: 100%;
    min-height: 0;
  }

  &:hover {
    border: none !important;
    outline: none !important;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.gray[200]};
  border-top-color: ${theme.colors.primary.accent};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ChartContentWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const InlineExpandButton = styled.button`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary.accent};
    outline-offset: 2px;
  }
`;

const ChartBreadcrumbsWrap = styled.div`
  padding: 0;
`;

const BarChartLegendWrap = styled.div<{ $noData?: boolean }>`
  padding: ${({ $noData }) => ($noData ? '24px 12px 24px 12px' : '0 12px 24px 12px')};
  margin-top: -2px;
`;

export const BarChart: React.FC<BarChartProps> = ({
  title,
  description,
  data,
  orientation = 'vertical',
  stacked = false,
  customTooltip,
  tooltipData,
  showBorder = true,
  height = 400,
  minHeight = 250,
  size = 'medium',
  loading = false,
  className,
  yAxisTickFormatter,
  yAxisTickValues,
  barSize,
  barWidth = 0.3,
  tooltipLabel,
  yAxisLabel,
  xAxisLabel,
  legendTooltipLabel,
  yAxisLabelStandoff = 0,
  tooltipFormatter,
  headerControls,
  style,
  onBarClick,
  legendFontSize = '10px',
  legendFontWeight = 500,
  xAxisLabelFontSize = 12,
  yAxisLabelFontSize = 12,
  totalChecks,
  period,
  categoryTimeRanges,
  xAxisTickFormat,
  onFullscreenChange,
  expandButtonAriaLabel,
  hideInlineExpandButton = false,
  onRegisterFullscreenToggle,
  fullscreenTargetRef,
  hideChartHeader = false,
  hideBuiltInLegend = false,
  hideHamburgerMenu = false,
  compactChartTitle = false,
  chartVisualPreset = 'default',
}) => {
  const chartLabels = i18n.monitors.detail.chartLabels;
  const resolvedLegendTooltipLabel = legendTooltipLabel ?? chartLabels.count;

  const chartRef = useRef<HTMLDivElement | null>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartKey, setChartKey] = useState(0);
  const isFullscreenRef = useRef(false);
  const prevFullscreenState = useRef<boolean | null>(null);
  const onFullscreenChangeRef = useRef(onFullscreenChange);
  onFullscreenChangeRef.current = onFullscreenChange;

  useEffect(() => {
    const handleFullscreenChange = () => {
      const target = fullscreenTargetRef?.current ?? chartRef.current;
      if (!target) return;
      const next = getDocumentFullscreenElement(document) === target;
      if (isFullscreenRef.current !== next) {
        isFullscreenRef.current = next;
        setIsFullscreen(next);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [fullscreenTargetRef]);

  useLayoutEffect(() => {
    if (prevFullscreenState.current === null) {
      prevFullscreenState.current = isFullscreen;
      return;
    }
    if (prevFullscreenState.current === isFullscreen) return;
    prevFullscreenState.current = isFullscreen;
    isFullscreenRef.current = isFullscreen;
    onFullscreenChangeRef.current?.(isFullscreen);
    setChartKey(k => k + 1);
  }, [isFullscreen]);

  const isCurrentlyFullscreen = useMemo(() => {
    if (typeof document === 'undefined') return isFullscreen;
    const target = fullscreenTargetRef?.current ?? chartRef.current;
    if (!target) return isFullscreen;
    return getDocumentFullscreenElement(document) === target || isFullscreen;
  }, [fullscreenTargetRef, isFullscreen]);

  const chartHeight = useMemo(() => {
    const currentlyFullscreen = isFullscreenRef.current || isFullscreen;
    if (currentlyFullscreen && typeof window !== 'undefined') {
      return window.innerHeight - 200;
    }
    return height;
  }, [isFullscreen, height]);

  const toggleBarFullscreen = useCallback(() => {
    const target = fullscreenTargetRef?.current ?? chartRef.current;
    if (!target || typeof window === 'undefined') return;
    const doc = document as FullscreenDocumentLike;
    const fullscreenElement = getDocumentFullscreenElement(doc);
    const container = target as FullscreenHTMLElement;
    if (fullscreenElement !== target) {
      requestElementFullscreen(container);
    } else {
      exitDocumentFullscreen(doc);
    }
  }, [fullscreenTargetRef]);

  useLayoutEffect(() => {
    if (!onRegisterFullscreenToggle) return;
    onRegisterFullscreenToggle(toggleBarFullscreen);
    return () => {
      onRegisterFullscreenToggle(() => {});
    };
  }, [onRegisterFullscreenToggle, toggleBarFullscreen]);

  if (loading && !isCurrentlyFullscreen) {
    return <ChartSkeleton type="bar" height={`${height}px`} items={data.categories?.length || 4} />;
  }

  const defaultColors = [...theme.colors.chart.barDefaultSeriesPalette];

  const maxValue = Math.max(...data.series.flatMap(s => s.data), 1);

  const chartData: ChartData[] = data.series.map((series, index) => {
    const createHoverTemplate = (value: number) => {
      const formattedValue = tooltipFormatter
        ? tooltipFormatter(value)
        : (value >= 1000
          ? `${(value / 1000).toFixed(1)}k`
          : value.toLocaleString());

      if (tooltipLabel) {
        return orientation === 'horizontal'
          ? `<b>%{fullData.name}</b><br>${tooltipLabel}: ${formattedValue}<extra></extra>`
          : `<b>%{fullData.name}</b><br>${tooltipLabel}: ${formattedValue}<extra></extra>`;
      }
      return orientation === 'horizontal'
        ? `<b>%{fullData.name}</b><br>%{y}: ${formattedValue}<extra></extra>`
        : `<b>%{fullData.name}</b><br>%{x}: ${formattedValue}<extra></extra>`;
    };

    return {
      x: orientation === 'vertical' ? data.categories : series.data,
      y: orientation === 'vertical' ? series.data : data.categories,
      type: 'bar',
      orientation: orientation === 'horizontal' ? 'h' : 'v',
      name: series.name,
      // Note: width property doesn't work in Plotly - bar width is controlled by barCategoryGap in layout
      showlegend: false, // Hide bar legend (we'll use scatter traces for circles)
      marker: {
        color: series.color || defaultColors[index],
        line: {
          color: 'transparent', // Remove border on hover
          width: 0, // No border
        },
      },
      hoverinfo: 'x+y+name',
      // Use customtext to pass formatted values, then reference in hovertemplate
      customdata: orientation === 'vertical'
        ? series.data.map(val => createHoverTemplate(val))
        : data.categories.map(() => createHoverTemplate(series.data[0] || 0)),
      hovertemplate: '%{customdata}',
    };
  });

  // Detect if each series has only one non-zero value at different positions
  // This pattern suggests we should use 'overlay' mode for better alignment
  // But if stacked is true, we always use 'stack' mode
  const hasSingleValuePerSeries = !stacked && data.series?.length > 0 && data.series.every(series => {
    const nonZeroIndices = series.data.map((val, idx) => val > 0 ? idx : -1).filter(idx => idx >= 0);
    return nonZeroIndices?.length === 1;
  });

  // Determine barmode: if stacked is true, always use 'stack' mode
  // Otherwise, use overlay for single value per series, or group for multiple series
  const effectiveBarmode = stacked ? 'stack' : (hasSingleValuePerSeries ? 'overlay' : 'group');

  // Create annotations to display values below each bar (for vertical orientation)
  // For grouped bars, show each bar's individual value
  const annotations = orientation === 'vertical'
    ? (() => {
      const annotationList: Array<Record<string, unknown>> = [];

      // If bars are grouped (multiple series with values at same categories), show each bar's value
      // Stacked bars show total value, not individual values
      if (!stacked && !hasSingleValuePerSeries && data.series?.length > 1) {
        // Grouped bars: create annotation for each series at each category
        data.categories.forEach((category, categoryIndex) => {
          data.series.forEach((series, seriesIndex) => {
            const value = series.data[categoryIndex] || 0;
            if (value > 0) {
              // Calculate x position offset for grouped bars
              // Bars are centered, so we need to offset based on series index
              const numSeries = data.series.length;
              const gap = 0.1; // Gap between bars
              const totalBarWidth = (barWidth * numSeries) + (gap * (numSeries - 1));
              const offset = (seriesIndex - (numSeries - 1) / 2) * (barWidth + gap);

              annotationList.push({
                x: category,
                xshift: offset * 100, // Convert to pixels (approximate)
                y: -maxValue * 0.04,
                text: value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toLocaleString(),
                showarrow: false,
                xref: 'x' as const,
                yref: 'y' as const,
                xanchor: 'center' as const,
                yanchor: 'middle' as const,
                font: {
                  size: 11,
                  color: theme.colors.gray[700],
                },
              });
            }
          });
        });
      } else if (hasSingleValuePerSeries) {
        // Single value per series at different positions: show each bar's value
        data.series.forEach((series, seriesIndex) => {
          const valueIndex = series.data.findIndex(val => val > 0);
          if (valueIndex >= 0 && valueIndex < data.categories?.length) {
            const value = series.data[valueIndex];
            annotationList.push({
              x: data.categories[valueIndex],
              y: -maxValue * 0.04,
              text: value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toLocaleString(),
              showarrow: false,
              xref: 'x' as const,
              yref: 'y' as const,
              xanchor: 'center' as const,
              yanchor: 'middle' as const,
              font: {
                size: 11,
                color: theme.colors.gray[700],
              },
            });
          }
        });
      } else {
        // Single series or stacked: show total value per category
        data.categories.forEach((category, categoryIndex) => {
          const totalValue = data.series.reduce((sum, series) => sum + (series.data[categoryIndex] || 0), 0);
          if (totalValue > 0) {
            annotationList.push({
              x: category,
              y: -maxValue * 0.04,
              text: totalValue >= 1000 ? `${(totalValue / 1000).toFixed(1)}k` : totalValue.toLocaleString(),
              showarrow: false,
              xref: 'x' as const,
              yref: 'y' as const,
              xanchor: 'center' as const,
              yanchor: 'middle' as const,
              font: {
                size: 11,
                color: theme.colors.gray[700],
              },
            });
          }
        });
      }

      return annotationList;
    })()
    : [];

  // Calculate maxBarSize
  // If hasSingleValuePerSeries is true, each series contributes one bar at a different category
  // So we don't need to multiply - just use the requested barSize
  // If bars are grouped (multiple bars per category), we need to account for that
  // Stacked bars are not grouped - they're stacked on top of each other
  const numSeries = data.series?.length;
  const isGroupedBars = !stacked && !hasSingleValuePerSeries && numSeries > 1;
  const calculatedMaxBarSize = barSize !== undefined && orientation === 'vertical'
    ? (isGroupedBars ? barSize * numSeries : barSize) // Only multiply if bars are actually grouped (not stacked)
    : barSize;

  // Convert barWidth to barCategoryGap and barGap
  // barWidth is a ratio (0-1) representing the proportion of category space bars should occupy
  // Smaller barWidth = narrower bars = larger gap
  // Larger barWidth = wider bars = smaller gap
  // barCategoryGap is the gap between categories (as percentage or number)
  // For vertical bars: if barWidth is provided, use it to calculate gap
  const calculateBarGaps = () => {
    if (orientation !== 'vertical') return {};

    // If barWidth is provided, use it to control bar width via gaps
    // barWidth of 0.1 means bars take 10% of category space, so gap is 90%
    // barWidth of 0.9 means bars take 90% of category space, so gap is 10%
    if (barWidth !== undefined) {
      // Convert barWidth (0-1) to gap percentage
      // Formula: gapPercentage = (1 - barWidth) * 100
      // Clamp barWidth to valid range (0.05 to 0.95) to avoid extreme values
      const clampedBarWidth = Math.max(0.05, Math.min(0.95, barWidth));
      const gapPercentage = (1 - clampedBarWidth) * 100;
      return {
        barCategoryGap: `${Math.max(0.1, Math.min(95, gapPercentage))}%`, // Clamp gap between 0.1% and 95%
        barGap: stacked ? 0 : (isGroupedBars ? 5 : 10), // No gap for stacked bars, small gap for grouped
      };
    }

    // Default behavior: use barSize-based gaps if barSize is set, otherwise use defaults
    if (barSize !== undefined) {
      return {
        barCategoryGap: isGroupedBars ? '2%' : '5%',
        barGap: isGroupedBars ? 10 : 30,
      };
    }

    return {};
  };

  // Check if x-axis has a label (for vertical orientation, use xAxisLabel if provided, otherwise empty)
  const xAxisTitleText =
    orientation === 'vertical' ? xAxisLabel || '' : xAxisLabel || chartLabels.barChartHorizontalValuesAxis;
  const xAxisHasLabel = xAxisTitleText && xAxisTitleText?.trim() !== '';
  const isSeverityDistributionPreset = chartVisualPreset === 'severityDistribution';
  const isPatternAnalysisPreset = chartVisualPreset === 'patternAnalysis';
  const yAxisMax = yAxisTickValues?.length
    ? yAxisTickValues[yAxisTickValues.length - 1]
    : undefined;

  const layout: ChartLayout = {
    height: chartHeight,
    barmode: effectiveBarmode,
    hovermode: 'closest',
    ...(isSeverityDistributionPreset ? { chartAppearance: { variant: 'severityDistribution' as const } } : {}),
    ...(isPatternAnalysisPreset ? { chartAppearance: { variant: 'patternAnalysis' as const } } : {}),
    ...(calculatedMaxBarSize !== undefined && !isPatternAnalysisPreset
      ? { barSize: calculatedMaxBarSize, maxBarSize: calculatedMaxBarSize }
      : {}),
    ...(isPatternAnalysisPreset ? {} : calculateBarGaps()),
    ...(isPatternAnalysisPreset
      ? {
          barSize: theme.layout.monitorDetail.patternAnalysisBarMaxSizePx,
          barGap: theme.layout.monitorDetail.patternAnalysisGroupedBarGapPx,
          barCategoryGap: '24%',
        }
      : {}),
    margin: {
      l: orientation === 'horizontal' ? 120 : isPatternAnalysisPreset ? 8 : yAxisLabel ? 20 : 40,
      r: 20,
      t: 10,
      b: orientation === 'horizontal'
        ? 60
        : (isSeverityDistributionPreset ? 28 : isPatternAnalysisPreset ? 32 : (xAxisHasLabel ? 50 : 40)),
    },
    annotations: annotations,
    xaxis: {
      showgrid: false,
      gridcolor: theme.colors.gray[200],
      title: {
        text: xAxisTitleText,
        font: { size: xAxisLabelFontSize, color: theme.colors.gray[700] },
      },
      type: orientation === 'vertical' ? 'category' : 'linear', // Use category type for proper alignment
      // Ensure proper spacing and alignment for category axis
      ...(orientation === 'vertical' && {
        categoryorder: 'array',
        categoryarray: data.categories,
      }),
      ...(isPatternAnalysisPreset
        ? {
            tickfont: {
              size: 14,
              color: theme.colors.pageScrapper.panels.nameUppercase,
              
            },
          }
        : {}),
      ...(xAxisTickFormat ? {
        tickFormatter: xAxisTickFormat,
      } : {}),
    },
    yaxis: {
      showgrid: !isPatternAnalysisPreset,
      gridcolor: theme.colors.gray[200],
      title: {
        text: yAxisLabel || (orientation === 'vertical' ? '' : chartLabels.barChartVerticalCategoriesAxis),
        font: { size: yAxisLabelFontSize, color: theme.colors.gray[700] },
        standoff: yAxisLabelStandoff,
        offset: yAxisLabelStandoff,
      },
      rangemode: 'tozero',
      automargin: false,
      ...(orientation === 'horizontal' && {
        automargin: true,
      }),
      ...(yAxisTickFormatter && {
        tickFormatter: yAxisTickFormatter,
      }),
      ...(yAxisTickValues?.length && yAxisMax != null
        ? {
            ticks: yAxisTickValues,
            domain: [0, yAxisMax] as [number, number],
            tickCount: yAxisTickValues.length,
          }
        : {}),
      ...(isPatternAnalysisPreset
        ? {
            tickfont: { size: 11, color: theme.colors.gray[500] },
          }
        : {}),
    },
    showlegend: false, // Hide legend, use custom legend instead
  };

  const config: ChartConfig = {
    displayModeBar: false,
    displaylogo: false,
    staticPlot: !zoomEnabled,
    responsive: true,
    scrollZoom: zoomEnabled,
    doubleClick: zoomEnabled ? 'reset' : false,
    modeBarButtonsToRemove: zoomEnabled
      ? ['lasso2d', 'select2d', 'autoScale2d', 'resetScale2d']
      : ['lasso2d', 'select2d', 'pan2d', 'zoom2d', 'autoScale2d', 'resetScale2d'],
  };

  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = [];
  data.categories.forEach((category, index) => {
    data.series.forEach(series => {
      // Include time range in brackets if available
      const timeRange = categoryTimeRanges?.[category];
      const categoryDisplay = timeRange ? `${category} (${timeRange})` : category;
      tableData.push({
        category: categoryDisplay,
        series: series.name,
        value: series.data[index]
      });
    });
  });

  const noData =
    chartData?.length === 0 ||
    chartData.every(trace => isPlotlyAxisEmpty(trace.x) && isPlotlyAxisEmpty(trace.y));

  return (
    <BarChartContainer ref={chartRef} $isFullscreen={isFullscreen} className={className} style={style}>
      {!hideChartHeader ? (
        <ChartHeader>
          <HeaderContent>
            {compactChartTitle ? (
              <CompactTitleRow>
                <CompactTitleText>{title}</CompactTitleText>
              </CompactTitleRow>
            ) : (
              <ChartBreadcrumbsWrap>
                <Breadcrumbs items={[{ label: title }]} />
              </ChartBreadcrumbsWrap>
            )}
            {description && <ChartDescription>{description}</ChartDescription>}
          </HeaderContent>
          <HeaderActions>
            {headerControls}
            {!hideInlineExpandButton && expandButtonAriaLabel ? (
              <InlineExpandButton type="button" aria-label={expandButtonAriaLabel} onClick={toggleBarFullscreen}>
                <ExpandIcon color={theme.colors.gray[600]} size={20} />
              </InlineExpandButton>
            ) : null}
            {!hideHamburgerMenu ? (
              <ChartHamburgerMenu
                chartTitle={title}
                tableData={tableData}
                chartContainerRef={chartRef}
                onZoomToggle={setZoomEnabled}
                initialZoomEnabled={zoomEnabled}
                totalChecks={totalChecks}
                period={period}
                showTableData={false}
              />
            ) : null}
          </HeaderActions>
        </ChartHeader>
      ) : null}

      <ChartContentWrapper>
        <ChartContent $minHeight={minHeight}>
          <Chart
            key={chartKey}
            data={chartData}
            layout={layout}
            config={config}
            revision={chartKey}
            tooltipLabel={tooltipLabel}
            tooltipFormatter={tooltipFormatter}
            size={size}
            customTooltip={customTooltip}
            tooltipData={tooltipData}
            onClick={onBarClick}
          />
        </ChartContent>
        {loading && isCurrentlyFullscreen && (
          <LoadingOverlay>
            <LoadingSpinner />
          </LoadingOverlay>
        )}
      </ChartContentWrapper>

      {!hideBuiltInLegend ? (
        <BarChartLegendWrap $noData={noData}>
          <CustomLegend
            series={data.series.map((series, index) => ({
              name: series.name,
              color: series.color || defaultColors[index],
              data: series.data,
            }))}
            size={size}
            legendTooltipLabel={resolvedLegendTooltipLabel}
            legendFontSize={legendFontSize}
            legendFontWeight={legendFontWeight}
            defaultColors={defaultColors}
          />
        </BarChartLegendWrap>
      ) : null}

    </BarChartContainer>
  );
};